import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCard,
  DocsCardGrid,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'prerequisites', title: '1. Before you start' },
  { id: 'create-chatbot', title: '2. Create your bot' },
  { id: 'add-knowledge', title: '3. Add knowledge' },
  { id: 'tune-behaviour', title: '4. Pick a model & prompt' },
  { id: 'customize-appearance', title: '5. Customise the widget' },
  { id: 'test-preview', title: '6. Test before you ship' },
  { id: 'deploy', title: '7. Deploy' },
  { id: 'next-steps', title: '8. Where to go next' },
];

const GettingStartedPage = () => {
  return (
    <>
      <NextSeo
        title="Your First AI Agent in 10 Minutes"
        description="Create, train, and deploy your first AI knowledge agent on Chat.co — a step-by-step walkthrough of the actual screens, from empty dashboard to live widget."
        canonical="https://chat.co/docs/getting-started"
        openGraph={{
          url: 'https://chat.co/docs/getting-started',
          title: 'Your First AI Agent in 10 Minutes | Chat.co',
          description:
            'Create, train, and deploy your first AI knowledge agent on Chat.co — a step-by-step walkthrough of the actual screens.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-08-30T00:00:00Z',
            section: 'Getting Started',
            tags: ['AI chatbot', 'tutorial', 'getting started', 'knowledge agent'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - Build Your First AI Agent',
            },
          ],
        }}
        twitter={{ cardType: 'summary_large_image' }}
      />

      <DocsArticleLayout
        category="Getting Started"
        categoryColor="text-green-700"
        title="Your first AI agent in 10 minutes"
        description="From an empty dashboard to a live, cited chat widget on your own site. Every step below names the exact screen you'll be on."
        readingTime="10 min read"
        lastUpdated="August 2026"
        tableOfContents={tableOfContents}
        nextArticle={{
          title: 'The knowledge upload playbook',
          href: PageLink.docsKnowledgeUpload,
        }}
        keyTakeaways={[
          'A bot is created empty — knowledge is added afterwards, from the Knowledge Base section',
          'Documents, crawled links, pasted text, Q&A pairs and audio are five separate source types',
          'Model, system prompt and creativity all live under Tune Bot → Behavior',
          'Test Bot exercises the real retrieval path, so it is a genuine rehearsal, not a mock',
          'Deployment gives you a script widget, an iframe, or a shareable hosted link',
        ]}>
        <p className="lead">
          By the end of this guide you&apos;ll have a working agent that answers from your own
          content, cites where each answer came from, and runs on your website. Ten minutes is a
          realistic estimate for a short document — a large PDF or a site crawl takes longer to
          process, but none of that time is yours to spend watching.
        </p>

        <section id="prerequisites" className="scroll-mt-28">
          <h2>1. Before you start</h2>
          <p>You need three things, and only one of them takes any effort:</p>
          <ul>
            <li>
              <strong>A Chat.co account.</strong> Sign in or sign up at{' '}
              <Link href={PageLink.login}>chat.co/auth/login</Link>.
            </li>
            <li>
              <strong>Something for the bot to know.</strong> A PDF, Word doc, spreadsheet or
              Markdown file — or just the URL of a site you want crawled.
            </li>
            <li>
              <strong>Access to your website&apos;s HTML</strong>, if you plan to embed the widget
              at the end. Not needed if you only want the hosted share link.
            </li>
          </ul>

          <DocsCallout variant="tip" title="Start small">
            Use a short, well-structured document for your first bot — a FAQ page, a product guide,
            a policy summary. One to ten pages is ideal. You can add everything else once you trust
            the setup, and a small corpus makes it obvious whether retrieval is working.
          </DocsCallout>
        </section>

        <section id="create-chatbot" className="scroll-mt-28">
          <h2>2. Create your bot</h2>
          <p>
            From the dashboard, open the bot selector at the top of the left sidebar and choose{' '}
            <strong>Create New Bot</strong>. You&apos;ll be offered two routes.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard icon="tabler:settings-2" title="Manual Setup">
              You name the bot, set its chat limit and visibility, and configure everything
              yourself. This is the path the rest of this guide follows.
            </DocsCard>
            <DocsCard icon="tabler:sparkles" title="AI Builder">
              Describe what you want in plain language and Chat.co assembles a starting
              configuration — persona, prompt and suggested sources — that you then refine.
            </DocsCard>
          </DocsCardGrid>

          <p>Choosing Manual Setup gives you a short form:</p>

          <DocsTable
            headers={['Field', 'What it does']}
            rows={[
              [
                <strong key="n">Bot Name</strong>,
                'How the bot appears in your sidebar and dashboard. Visitors see the display name you set later under Appearance, not this one.',
              ],
              [
                <strong key="c">Chat Limit</strong>,
                'Caps how many messages this bot may consume from your account allocation. Useful when you run several bots and want one of them ring-fenced.',
              ],
              [
                <strong key="v">Public / Private</strong>,
                'Private bots are reachable only by you and collaborators you invite. A private bot cannot be embedded publicly — switch it to Public in Settings when you are ready to deploy.',
              ],
            ]}
          />

          <DocsCallout variant="note" title="No model choice here — that's deliberate">
            New bots start on a sensible default and you pick the model afterwards, in{' '}
            <strong>Tune Bot → Behavior</strong>. That keeps creation to one screen and lets you
            swap models later without rebuilding anything.
          </DocsCallout>
        </section>

        <section id="add-knowledge" className="scroll-mt-28">
          <h2>3. Add knowledge</h2>
          <p>
            Your bot exists but knows nothing. Open it, then look at the{' '}
            <strong>Knowledge Base</strong> group in the sidebar. There are five source types, and
            they are genuinely different things — not five doors to the same upload box.
          </p>

          <DocsCardGrid columns={3}>
            <DocsCard icon="tabler:file-text" title="Documents">
              File uploads. PDF, DOC, DOCX, XLS, XLSX, CSV and Markdown, up to ten files at a time.
            </DocsCard>
            <DocsCard icon="tabler:link" title="Links">
              Crawl a website or a single page. You review the discovered pages and choose which
              ones to keep before anything is indexed.
            </DocsCard>
            <DocsCard icon="tabler:notes" title="Text">
              Paste raw text directly. Best for policies and snippets that live in someone&apos;s
              head rather than in a file.
            </DocsCard>
            <DocsCard icon="tabler:help-circle" title="Q&amp;A">
              Explicit question/answer pairs. These take priority over retrieved passages, so use
              them to pin an exact wording.
            </DocsCard>
            <DocsCard icon="tabler:microphone" title="Audio">
              Upload recordings and Chat.co transcribes them into searchable knowledge.
            </DocsCard>
          </DocsCardGrid>

          <h3>Uploading your first document</h3>

          <DocsSteps>
            <DocsStep title="Open Knowledge Base → Documents">
              <p>
                With your bot selected, click <strong>Documents</strong> under Knowledge Base in the
                sidebar.
              </p>
            </DocsStep>
            <DocsStep title="Drop the file in">
              <p>
                Drag files onto the dropzone or click to browse. Accepted types are{' '}
                <code>.pdf</code>, <code>.doc</code>, <code>.docx</code>, <code>.xls</code>,{' '}
                <code>.xlsx</code>, <code>.csv</code> and <code>.md</code>. Ten files per batch; the
                per-file size ceiling comes from your plan.
              </p>
            </DocsStep>
            <DocsStep title="Wait for processing">
              <p>
                Each file is extracted, chunked and indexed. Short documents finish in well under a
                minute; long scanned PDFs take longer because the text has to be recognised first.
                The row shows its state as it goes.
              </p>
            </DocsStep>
            <DocsStep title="Confirm it landed">
              <p>
                A completed row means the content is retrievable. If a row fails, the{' '}
                <Link href={PageLink.docsTroubleshooting}>troubleshooting guide</Link> covers the
                usual causes — password-protected PDFs and image-only scans being the two most
                common.
              </p>
            </DocsStep>
          </DocsSteps>

          <DocsCallout variant="warning" title="Plain .txt is not an upload type">
            There is no <code>.txt</code> file upload — that content belongs in{' '}
            <strong>Knowledge Base → Text</strong>, where you paste it directly. Rename a{' '}
            <code>.txt</code> to <code>.md</code> if you would rather upload it as a file.
          </DocsCallout>
        </section>

        <section id="tune-behaviour" className="scroll-mt-28">
          <h2>4. Pick a model and write the prompt</h2>
          <p>
            Go to <strong>Tune Bot</strong> in the sidebar, then the <strong>Behavior</strong> view.
            Three controls matter here.
          </p>

          <DocsTable
            headers={['Control', 'What to do with it']}
            rows={[
              [
                <strong key="m">AI Model</strong>,
                'Chat.co offers models from OpenAI, Anthropic, Google, xAI and several open-weight families. For a first bot, take a current mid-tier model — they are fast, cheap per message, and grounded answers depend far more on your knowledge base than on the model tier.',
              ],
              [
                <strong key="p">Instruction Prompt</strong>,
                'The system prompt: who the bot is, what it refuses, what tone it uses. The single highest-leverage field on this page. There is an Enhance action that rewrites a rough draft into something more specific.',
              ],
              [
                <strong key="t">Creativity</strong>,
                'Temperature. Keep it low for factual support bots — you want the model to stay close to the retrieved text rather than improvise around it.',
              ],
            ]}
          />

          <DocsCallout variant="tip" title="Say what to do when the answer isn't there">
            The most valuable sentence in most system prompts is the one covering absence: tell the
            bot explicitly to say it doesn&apos;t know and offer to hand off, rather than
            constructing a plausible answer. See{' '}
            <Link href={PageLink.docsCitationQuality}>citation quality</Link> for the full
            treatment.
          </DocsCallout>
        </section>

        <section id="customize-appearance" className="scroll-mt-28">
          <h2>5. Customise the widget</h2>
          <p>
            Open <strong>Appearance</strong> in the sidebar. It&apos;s a guided builder with a live
            preview beside it, split into five steps:
          </p>

          <DocsTable
            headers={['Step', 'Covers']}
            rows={[
              [
                <strong key="1">Basics</strong>,
                'Display name, welcome message, suggested questions',
              ],
              [
                <strong key="2">Brand Style</strong>,
                'Avatar, colours, which side the launcher sits on, notification sounds',
              ],
              [
                <strong key="3">Lead Capture</strong>,
                'Whether and when the chat asks visitors for contact details',
              ],
              [
                <strong key="4">Chat Features</strong>,
                'Which models visitors may pick, whether sources and file uploads are shown, voice input',
              ],
              [<strong key="5">Branding</strong>, 'Footer branding controls'],
            ]}
          />

          <p>
            Suggested questions are worth more attention than they look. An empty chat box is a hard
            prompt for a first-time visitor; three good starter questions are the difference between
            a bot that gets used and one that gets closed.
          </p>
        </section>

        <section id="test-preview" className="scroll-mt-28">
          <h2>6. Test before you ship</h2>
          <p>
            Use <strong>Test Bot</strong> to talk to the agent exactly as a visitor would — same
            retrieval, same model, same prompt. Work through four kinds of question:
          </p>

          <DocsSteps>
            <DocsStep title="Questions you know are answered">
              <p>
                Straight from the document you uploaded. These should come back correct and cited.
                If they don&apos;t, the problem is retrieval, not the model.
              </p>
            </DocsStep>
            <DocsStep title="Questions phrased the way customers phrase them">
              <p>
                Not the document&apos;s wording — your customers&apos;. &ldquo;Can I get my money
                back?&rdquo; rather than &ldquo;What is the refund policy?&rdquo;
              </p>
            </DocsStep>
            <DocsStep title="Questions with no answer in your content">
              <p>
                The bot should decline cleanly. If it invents an answer, tighten the system prompt
                before you deploy — this is the failure that costs trust.
              </p>
            </DocsStep>
            <DocsStep title="Check the citations, not just the text">
              <p>
                Open a citation and confirm it points at the passage the claim came from. A correct
                answer with a wrong citation is a bug you want to find now.
              </p>
            </DocsStep>
          </DocsSteps>
        </section>

        <section id="deploy" className="scroll-mt-28">
          <h2>7. Deploy</h2>
          <p>
            Open <strong>Deployment</strong> in the sidebar. Three delivery methods, plus a security
            tab.
          </p>

          <DocsCardGrid columns={3}>
            <DocsCard icon="tabler:share-2" title="Share Link">
              A hosted chat page you can send to anyone. No code, no website required.
            </DocsCard>
            <DocsCard icon="tabler:code" title="Website Widget">
              The floating launcher. One script tag, customisable before you copy it.
            </DocsCard>
            <DocsCard icon="tabler:layout-grid" title="Embedded Chat Box">
              An iframe you place inline in a page, sized to fit your layout.
            </DocsCard>
          </DocsCardGrid>

          <h3>The widget script</h3>
          <p>
            On the <strong>Website Widget</strong> tab you design the launcher first — position,
            colour, auto-open behaviour — and the snippet updates as you go. Copy it and paste it
            before your closing <code>&lt;/body&gt;</code> tag:
          </p>

          <DocsCodeBlock
            language="html"
            filename="your-site.html"
            code={`<script defer src="https://chat.co/api/embed" data-bot-id="YOUR_BOT_ID"></script>`}
          />

          <p>
            If you customised the launcher, the copied snippet also carries a{' '}
            <code>window.ChatCoEmbed</code> configuration block above the script tag. Paste both
            lines — the config has to come first.
          </p>

          <DocsCallout variant="warning" title="A private bot will not load on your site">
            If Deployment shows a red &ldquo;this bot is not public&rdquo; banner, the widget will
            refuse to render for visitors. Switch visibility to Public in <strong>Settings</strong>{' '}
            first. Full detail, including domain allowlisting, is in{' '}
            <Link href={PageLink.docsWebsiteIntegration}>website integration</Link>.
          </DocsCallout>
        </section>

        <section id="next-steps" className="scroll-mt-28">
          <h2>8. Where to go next</h2>
          <p>
            You have a working agent. What you do in the first week after that determines whether it
            stays useful.
          </p>

          <DocsCardGrid columns={2}>
            <DocsCard
              icon="tabler:upload"
              title="Knowledge upload playbook"
              href={PageLink.docsKnowledgeUpload}>
              Which source type for which content, and how to keep a growing knowledge base clean.
            </DocsCard>
            <DocsCard
              icon="tabler:bulb"
              title="Analytics, ROI &amp; knowledge gaps"
              href={PageLink.docsAnalyticsRoi}>
              Find the questions your bot failed to answer and turn them into content.
            </DocsCard>
            <DocsCard icon="tabler:headset" title="Human handoff" href={PageLink.docsHumanHandoff}>
              Give visitors a route to a person when the bot reaches its limit.
            </DocsCard>
            <DocsCard
              icon="tabler:plug-connected"
              title="Channel integrations"
              href={PageLink.docsChannelIntegrations}>
              Put the same agent in Slack, Teams, WhatsApp or your helpdesk.
            </DocsCard>
          </DocsCardGrid>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default GettingStartedPage;
